import { wizardModules, WizardModule } from '@/mocks/wizard';

interface StepModulesProps {
  selected: string[];
  onChange: (selected: string[]) => void;
  userRole: string;
}

function getBadgeStyle(badge: WizardModule['badge'], isSelected: boolean) {
  if (badge === 'included') {
    return { bg: '#E8F5EE', color: '#2E7D32', border: 'rgba(46,125,50,0.25)', label: '✓ Включено' };
  }
  if (badge === 'trial') {
    return { bg: '#FFF8E1', color: '#B8860B', border: 'rgba(184,134,11,0.3)', label: '14 днів trial' };
  }
  if (isSelected) {
    return { bg: '#FFF0F0', color: '#D12229', border: 'rgba(209,34,41,0.3)', label: '✓ Обрано' };
  }
  return { bg: '#F5F5F7', color: '#6B6B74', border: '#E5E5E8', label: 'Доступно' };
}

function ModuleCard({ mod, isSelected, onToggle }: {
  mod: WizardModule;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const isMandatory = mod.badge === 'included';
  const isTrial = mod.badge === 'trial';
  const badgeStyle = getBadgeStyle(mod.badge, isSelected);

  return (
    <div
      onClick={() => { if (!isMandatory) onToggle(); }}
      className="rounded-xl p-4 flex flex-col gap-3 transition-all"
      style={{
        background: '#fff',
        border: `2px solid ${isSelected || isMandatory ? '#D12229' : '#E5E5E8'}`,
        cursor: isMandatory ? 'default' : 'pointer',
        opacity: 1,
      }}
      onMouseEnter={(e) => {
        if (!isMandatory) e.currentTarget.style.borderColor = isSelected ? '#D12229' : '#CCCCCC';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isSelected || isMandatory ? '#D12229' : '#E5E5E8';
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: mod.iconBg }}
        >
          <i className={`${mod.iconClass} text-sm`} style={{ color: mod.iconColor }} />
        </div>
        <span
          className="text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{
            background: badgeStyle.bg,
            color: badgeStyle.color,
            border: `1px solid ${badgeStyle.border}`,
          }}
        >
          {isTrial ? badgeStyle.label : badgeStyle.label}
        </span>
      </div>

      {/* Name & code */}
      <div>
        <p className="text-[15px] font-bold mb-0.5" style={{ color: '#1A1A1A' }}>{mod.name}</p>
        <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: '#AFAFB8' }}>
          {mod.code} · {mod.status === 'mandatory' ? "ОБО'ЯЗКОВИЙ" : mod.status === 'new' ? 'NEW' : 'ІСНУЮЧИЙ'}
        </p>
      </div>

      {/* Description */}
      <p className="text-xs leading-relaxed flex-1" style={{ color: '#6B6B74' }}>
        {mod.description}
      </p>

      {/* Price row */}
      <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid #F0F0F2' }}>
        <span className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>{mod.price}</span>
        {isTrial && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className="px-3 py-1 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
            style={{
              background: isSelected ? '#D12229' : '#F5F5F7',
              color: isSelected ? '#fff' : '#1A1A1A',
              border: `1px solid ${isSelected ? '#D12229' : '#E5E5E8'}`,
            }}
          >
            {isSelected ? '✓ Trial' : 'Trial'}
          </button>
        )}
        {!isTrial && !isMandatory && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className="px-3 py-1 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
            style={{
              background: isSelected ? '#D12229' : '#F5F5F7',
              color: isSelected ? '#fff' : '#1A1A1A',
              border: `1px solid ${isSelected ? '#D12229' : '#E5E5E8'}`,
            }}
          >
            {isSelected ? '− Прибрати' : '+ Додати'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function StepModules({ selected, onChange, userRole }: StepModulesProps) {
  const toggle = (id: string) => {
    const mod = wizardModules.find((m) => m.id === id);
    if (!mod || mod.badge === 'included') return;
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const totalPrice = wizardModules
    .filter((m) => selected.includes(m.id) || m.badge === 'included')
    .reduce((acc, m) => {
      const match = m.price.match(/€(\d+)/);
      return acc + (match ? parseInt(match[1], 10) : 0);
    }, 0);

  const selectedCount = selected.length;

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }}>
          Оберіть модулі для старту
        </h2>
        <p className="text-sm" style={{ color: '#8B8B94' }}>
          Модулі можна додати або прибрати пізніше. Tier A add-ons — безкоштовний 14-денний trial.
        </p>
      </div>

      {/* Recommendation banner */}
      <div
        className="mb-6 p-4 rounded-lg"
        style={{
          background: '#FFF8F8',
          borderLeft: '3px solid #D12229',
          border: '1px solid rgba(209,34,41,0.15)',
          borderLeftWidth: '3px',
        }}
      >
        <p className="text-sm" style={{ color: '#1A1A1A' }}>
          <span className="mr-1">🎯</span>
          <strong>Рекомендовано для ролі &quot;{userRole}&quot;:</strong>{' '}
          Core + Forecast + Dispatch + Reports ={' '}
          <strong style={{ color: '#D12229' }}>€485/місяць</strong>{' '}
          <span style={{ color: '#8B8B94' }}>(перший місяць -15%)</span>
        </p>
      </div>

      {/* Module grid */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {wizardModules.map((mod) => (
          <ModuleCard
            key={mod.id}
            mod={mod}
            isSelected={selected.includes(mod.id) || mod.badge === 'included'}
            onToggle={() => toggle(mod.id)}
          />
        ))}
      </div>

      {/* Summary bar */}
      <div
        className="flex items-center justify-between p-4 rounded-xl"
        style={{ background: '#F7F7F9', border: '1px solid #E5E5E8' }}
      >
        <div>
          <p className="text-sm" style={{ color: '#6B6B74' }}>
            Обрано: <strong style={{ color: '#1A1A1A' }}>{selectedCount} модулів</strong>
            {' · '}
            <span style={{ color: '#8B8B94' }}>Перший місяць</span>
          </p>
          <p className="text-2xl font-bold mt-0.5" style={{ color: '#D12229', letterSpacing: '-0.02em' }}>
            €{totalPrice.toLocaleString('uk-UA')}
          </p>
        </div>
        <p className="text-xs text-right" style={{ color: '#AFAFB8', maxWidth: '220px' }}>
          Ціни орієнтовні. Фінальний рахунок — після налаштування та підтвердження.
        </p>
      </div>
    </div>
  );
}
